import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daeno9jep053ijub1eo0";

type FormState = "idle" | "loading" | "success" | "error";

export default function SubscribeSection() {
  const [state, setState] = useState<FormState>("idle");
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("订阅吃瓜提醒");
  const [channel, setChannel] = useState("剧集瓜、综艺瓜实时动态");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("website_alt") as string) || "";

    // 蜜罐命中：静默按成功处理，不真正提交
    if (honeypot.trim() !== "") {
      setState("success");
      setMsg("收到啦，订阅请求已记录，感谢关注！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      "input,select,textarea"
    ).forEach((el) => {
      if (el.name && el.name !== "website_alt" && (el as HTMLInputElement).value !== "") {
        payload.append(el.name, (el as HTMLInputElement).value);
      }
    });

    setState("loading");
    setMsg("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || "";
      if (!res.ok || !parsed || parsed.code !== "OK" || /spam/i.test(serverMsg)) {
        setState("error");
        setMsg(serverMsg || "提交失败，请稍后重试。");
        return;
      }
      setState("success");
      setMsg("订阅成功！最新瓜料与实时动态将第一时间推送到你的邮箱。");
      form.reset();
    } catch {
      setState("error");
      setMsg("网络异常，请稍后再试。");
    }
  }

  return (
    <section id="subscribe" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid overflow-hidden rounded-2xl border border-background-200 bg-foreground-950 lg:grid-cols-2">
          <div className="p-7 md:p-10">
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
              <i className="ri-mail-send-line"></i>订阅吃瓜提醒
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black leading-tight text-background-50 md:text-3xl">
              实时动态太多？
              <br />
              把最值得吃的那口推送给你
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-background-300 md:text-base">
              填写你关注的栏目与常用邮箱，我们将实时更新中的重点话题与最新进展整理成简报，第一时间送达，让你不错过任何一条新鲜爆料。
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-background-200">
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>热门话题实时播报，早看早知道
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>支持自定义关注栏目与推送节奏
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-accent-400"></i>随时可退订，不再有打扰
              </li>
            </ul>
          </div>

          <div className="flex items-center border-t border-background-800 bg-background-100 p-7 md:p-10 lg:border-l lg:border-t-0">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="w-full space-y-4"
              aria-label="订阅吃瓜提醒"
            >
              <div>
                <label htmlFor="sub-email" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  常用邮箱
                </label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label htmlFor="sub-subject" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  订阅主题
                </label>
                <select
                  id="sub-subject"
                  name="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option>订阅吃瓜提醒</option>
                  <option>投稿一手消息</option>
                  <option>商务与合作</option>
                </select>
              </div>
              <div>
                <label htmlFor="sub-channel" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  关注栏目
                </label>
                <select
                  id="sub-channel"
                  name="channel"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option>剧集瓜、综艺瓜实时动态</option>
                  <option>偶像瓜、音乐瓜实时动态</option>
                  <option>动漫瓜、时尚瓜实时动态</option>
                  <option>全部栏目重点速览</option>
                </select>
              </div>
              <div>
                <label htmlFor="sub-msg" className="mb-1.5 block text-sm font-semibold text-foreground-800">
                  留言 / 线索说明（选填）
                </label>
                <textarea
                  id="sub-msg"
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="如果你想投稿或反馈，可在这里描述要点……"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              {/* 蜜罐字段 */}
              <div className="website-field-guard" aria-hidden="true">
                <label htmlFor="website_alt">网站地址</label>
                <input id="website_alt" type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              {state === "success" && (
                <p className="flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900" role="status">
                  <i className="ri-checkbox-circle-fill mt-0.5"></i>
                  {msg}
                </p>
              )}
              {state === "error" && (
                <p className="flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-800" role="alert">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {msg}
                </p>
              )}

              <button
                type="submit"
                disabled={state === "loading"}
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {state === "loading" ? "提交中…" : "立即订阅 / 提交"}
                {state !== "loading" && <i className="ri-send-plane-line ml-1.5"></i>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}